import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { getRequest, postRequest, deleteRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { TabView, TabPanel } from 'primereact/tabview';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { DateTime } from 'luxon';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/sk';
import 'i18n-iso-countries/langs/en';
import 'i18n-iso-countries/langs/de';
import AuthContext from '../../components/AuthContext';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { customerClientTypeDisplayLabel } from '../../utils/customerClientTypeDisplayLabel';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { tabulatorDateUserColumnMinWidth } from '../../utils/tabulatorColumnMinWidth';
import CustomerBusinessStatusPanel from '../../components/crm/CustomerBusinessStatusPanel';
import CrmCommunicationTicketsList, {
  CRM_COMM_MASK_MY_AGENDA,
} from './CrmCommunicationTicketsList';
import {
  CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_COMMUNICATION,
  CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_QUOTES,
} from '../../utils/crmCommunicationTableColumns';
import { PrintIcon, AddIcon, CopyIcon, EditIcon, DeleteIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon } from '../svg/svgIcons';
import { createArrayNavigateHandlers } from '../../utils/arrayNavigate';
import { activeCrmTraderUsers } from '../../utils/crmTraders';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';

const PAGE_ADD_KEY = 'CustomerAllPartnersDetail';
const PAGE_BUYERS = 'CustomerAllPartnersDetail_Buyers';
countries.registerLocale(require('i18n-iso-countries/langs/sk'));
countries.registerLocale(require('i18n-iso-countries/langs/en'));
countries.registerLocale(require('i18n-iso-countries/langs/de'));

function normalizeInitialValues(row) {
  const types = Array.isArray(row?.types) ? row.types : [];
  const statusByType = row?.partner_status_by_type && typeof row.partner_status_by_type === 'object'
    ? row.partner_status_by_type
    : {};
  const partyMode = row?.party_mode
    || statusByType?.party_mode
    || (types.includes('customer') ? 'customer' : (types.includes('client') ? 'client' : 'none'));
  return {
    id: row?.id || null,
    name: row?.name || '',
    defined_id: row?.defined_id || '',
    country: row?.country || '',
    address: row?.address || '',
    tel_number: row?.tel_number || '',
    email: row?.email || '',
    web_address: row?.web_address || '',
    contact_person: row?.contact_person || '',
    description: row?.description || '',
    types,
    party_mode: partyMode,
    customer_state: row?.customer_state || 'free',
    trader_id: row?.trader_id || null,
    trader_name: row?.trader_name || '',
    execution_user_id: row?.execution_user_id || null,
    execution_user_name: row?.execution_user_name || '',
    deadline: row?.deadline || null,
    deadline_reminder_comment_id: row?.deadline_reminder_comment_id ?? null,
    customer_type: row?.customer_type || statusByType?.customer || '',
    client_type: row?.client_type || statusByType?.client || '',
    created_by_name: row?.created_by_name || '',
    updated_by_name: row?.updated_by_name || '',
    created_at: row?.created_at || null,
    updated_at: row?.updated_at || null,
    comments: Array.isArray(row?.comments) ? row.comments : [],
    mentions: Array.isArray(row?.mentions) ? row.mentions : [],
    supervisor_user_ids: Array.isArray(row?.supervisor_user_ids) ? row.supervisor_user_ids : [],
  };
}

function countryLabelFromCode(countryCode, locale) {
  if (!countryCode) return '';
  const lang = locale === 'de' ? 'de' : (locale === 'en' || locale === 'gb' ? 'en' : 'sk');
  return countries.getName(countryCode, lang) || countryCode;
}

function formatBuyerDateTime(value) {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('sk-SK');
}

function buyerHeaderTitle(label) {
  return `<span style="font-size:14px;font-weight:600;">${label}</span>`;
}

export default function CustomerAllPartnersDetail({ customerId, customerRow = null, type = 'edit' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { t, locale } = useContext(LocaleContext);
  const { toast, openFavoriteDialog, setFastView, setDialogText, setVisible, setUserMaskFilter, getDateFormat, formatDate, getUserInitials, getUserColor } = useContext(UtilContext);
  const [formType, setFormType] = useState(type);
  const [closeTab, setCloseTab] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  const [contacts, setContacts] = useState([]);
  const buyersTableRef = useRef(null);
  /** Contacts fetch UI — refs only (avoids race with Tabulator mount). */
  const contactsLoadingRef = useRef(false);
  const contactsLoadErrorRef = useRef(false);
  /** Zoznam pre šípky prev/next — načíta sa po detaile (idle), alebo hneď pri openById. */
  const navListBootstrappedRef = useRef(false);
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisibleBuyers, setColumnsVisibleBuyers] = useState({
    id: true,
    name: true,
    description: true,
    tel_number: true,
    email: true,
    created_at: true,
    updated_at: true,
  });
  const [editBuyerDisabled, setEditBuyerDisabled] = useState(true);
  const [deleteBuyerDisabled, setDeleteBuyerDisabled] = useState(true);
  const [contactDialogVisible, setContactDialogVisible] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', tel_number: '', email: '', description: '' });
  const { user } = useContext(AuthContext);
  const [detailActivityLoading, setDetailActivityLoading] = useState(false);
  const submitRef = useRef(null);
  const isRestoringBuyerMaskRef = useRef(false);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const customerClientTypeOptions = useMemo(() => ([
    { value: 'new_client', label: t('CustomerTypeOptions.NewClient') },
    { value: 'potential_client', label: t('CustomerTypeOptions.PotentialClient') },
    { value: 'verified_client', label: t('CustomerTypeOptions.VerifiedClient') },
    { value: 'inquiring_client', label: t('CustomerTypeOptions.InquiringClient') },
    { value: 'occasional_customer', label: t('CustomerTypeOptions.OccasionalCustomer') },
    { value: 'regular_customer', label: t('CustomerTypeOptions.RegularCustomer') },
    { value: 'strategic_customer', label: t('CustomerTypeOptions.StrategicCustomer') },
    { value: 'lost_customer', label: t('CustomerTypeOptions.LostCustomer') },
  ]), [t]);
  const customerTypeValues = useMemo(() => new Set(['occasional_customer', 'regular_customer', 'strategic_customer', 'lost_customer']), []);
  const clientTypeValues = useMemo(() => new Set(['new_client', 'potential_client', 'verified_client', 'inquiring_client']), []);
  const customerTypeOptions = useMemo(
    () => customerClientTypeOptions.filter((opt) => customerTypeValues.has(opt.value)),
    [customerClientTypeOptions, customerTypeValues]
  );
  const clientTypeOptions = useMemo(
    () => customerClientTypeOptions.filter((opt) => clientTypeValues.has(opt.value)),
    [customerClientTypeOptions, clientTypeValues]
  );
  const loadRows = useCallback(async () => {
    const rowsResp = await getRequest('/api/customer_evaluations/all_partners/paged?size=200');
    setAllRows(Array.isArray(rowsResp?.data) ? rowsResp.data : []);
  }, []);

  const ensureNavListLoaded = useCallback(async () => {
    if (navListBootstrappedRef.current) return;
    navListBootstrappedRef.current = true;
    try {
      await loadRows();
    } catch (_) {
      navListBootstrappedRef.current = false;
    }
  }, [loadRows]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return null;
    const resp = await getRequest(`/api/customer_evaluations/my_customers/${id}`);
    const payload = resp?.data ?? resp;
    return payload?.data ?? payload ?? null;
  }, []);

  const syncBuyersTableAlert = useCallback(() => {
    const table = buyersTableRef.current?.table ?? buyersTableRef.current;
    if (!table) return;
    if (contactsLoadingRef.current) {
      table.alert?.(t('Common.LoadingData'));
      return;
    }
    if (contactsLoadErrorRef.current) {
      table.alert?.(t('Common.LoadFailed'));
      return;
    }
    table.clearAlert?.();
  }, [t]);

  const loadContacts = useCallback(async (id) => {
    if (!id) return;
    contactsLoadingRef.current = true;
    contactsLoadErrorRef.current = false;
    syncBuyersTableAlert();
    try {
      const resp = await getRequest(`/api/customer_evaluations/my_customers/${id}/contacts`);
      const payload = resp?.data ?? resp;
      const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      setContacts(rows);
    } catch (e) {
      contactsLoadErrorRef.current = true;
      throw e;
    } finally {
      contactsLoadingRef.current = false;
      syncBuyersTableAlert();
    }
  }, [syncBuyersTableAlert]);

  const bindBuyersTableRef = useCallback((r) => {
    buyersTableRef.current = r?.current ?? r ?? null;
    syncBuyersTableAlert();
  }, [syncBuyersTableAlert]);

  const normalizeText = useCallback((value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(), []);
  const dateOrUserFilter = useCallback((headerValue, _rowValue, rowData, filterParams) => {
    if (!headerValue || String(headerValue).trim() === '') return true;
    const raw = String(headerValue).trim();
    if (raw.startsWith('range:')) {
      const [, fromRaw = '', toRaw = ''] = raw.match(/^range:([^:]+):([^:]+)$/) || [];
      if (!fromRaw || !toRaw) return true;
      const from = DateTime.fromFormat(fromRaw, 'yyyy-MM-dd').startOf('day');
      const to = DateTime.fromFormat(toRaw, 'yyyy-MM-dd').endOf('day');
      if (!from.isValid || !to.isValid) return true;
      const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
      if (!dt.isValid) return false;
      return dt >= from && dt <= to;
    }
    const needle = normalizeText(raw);
    const userObj = filterParams?.name === 'created_at' ? rowData?.created_by_user : rowData?.updated_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    if (!dt.isValid) return normalizeText(`${uname}`).includes(needle);
    return normalizeText(`${uname} ${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}`).includes(needle);
  }, [dateFmt, dateTimeFmt, normalizeText]);

  useEffect(() => {
    setFormType(type);
  }, [type]);

  useLayoutEffect(() => {
    if (!customerId) {
      setDetailActivityLoading(false);
      return;
    }
    setDetailActivityLoading(true);
  }, [customerId]);

  const formik = useFormik({
    initialValues: normalizeInitialValues(null),
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.name || !String(values.name).trim()) errors.name = t('Common.Required');
      return errors;
    },
    onSubmit: async (values) => {
      if (!values.id) return;
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      try {
        const payload = {
          name: values.name,
          country: values.country || null,
          address: values.address || null,
          tel_number: values.tel_number || null,
          email: values.email || null,
          web_address: values.web_address || null,
          contact_person: values.contact_person || null,
          description: values.description || null,
        };
        const resp = await postRequest(`/api/customer_evaluations/my_customers/update/${values.id}`, payload, true);
        formik.setValues(normalizeInitialValues(resp?.data || values));
        await loadRows();
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerCentralDatabaseAdd.Toast.Updated'), life: 2500 });
        if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3500 });
      } finally {
        setVisible?.(false);
        setCloseTab(false);
      }
    },
  });
  const displayCustomerType = useMemo(() => (
    formik.values.party_mode === 'customer'
      ? (formik.values.customer_type || formik.values.client_type || '')
      : (formik.values.customer_type || '')
  ), [formik.values.customer_type, formik.values.client_type, formik.values.party_mode]);
  const displayClientType = useMemo(() => (
    formik.values.party_mode === 'client'
      ? (formik.values.client_type || formik.values.customer_type || '')
      : (formik.values.client_type || '')
  ), [formik.values.customer_type, formik.values.client_type, formik.values.party_mode]);

  const [allUsersForMentions, setAllUsersForMentions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const fromStore = state?.users;
    if (Array.isArray(fromStore) && fromStore.length > 0) {
      setAllUsersForMentions(fromStore);
      return () => { cancelled = true; };
    }
    getRequest('/api/users')
      .then((resp) => {
        if (!cancelled) setAllUsersForMentions(Array.isArray(resp?.data) ? resp.data : []);
      })
      .catch(() => {
        if (!cancelled) setAllUsersForMentions([]);
      });
    return () => { cancelled = true; };
  }, [state.users]);

  const usersForMentions = useMemo(() => {
    const extraIds = [
      formik.values?.execution_user_id,
      ...(formik.values?.mentions || []).map((m) => m?.id),
    ];
    return activeCrmTraderUsers(state?.crm_traders || [], allUsersForMentions, extraIds);
  }, [allUsersForMentions, formik.values?.execution_user_id, formik.values?.mentions, state?.crm_traders]);

  const canManageInterestedUsers = useMemo(() => {
    const uid = user?.id != null ? Number(user.id) : 0;
    const tid = formik.values?.trader_id != null ? Number(formik.values.trader_id) : 0;
    if (uid > 0 && tid > 0 && uid === tid) return true;
    if ((user?.permissions || []).includes('access customer_management-complete-list')) return true;
    const mentionIds = new Set((formik.values?.mentions || []).map((m) => Number(m?.id)));
    if (uid > 0 && mentionIds.has(uid)) return true;
    return false;
  }, [user, formik.values?.trader_id, formik.values?.mentions]);

  const canRemoveInterestedUsers = useMemo(() => {
    const uid = user?.id != null ? Number(user.id) : 0;
    const tid = formik.values?.trader_id != null ? Number(formik.values.trader_id) : 0;
    if (uid > 0 && tid > 0 && uid === tid) return true;
    const supervisorIds = new Set((formik.values?.supervisor_user_ids || []).map((id) => Number(id)));
    return uid > 0 && supervisorIds.has(uid);
  }, [user?.id, formik.values?.trader_id, formik.values?.supervisor_user_ids]);

  const mergeDetailIntoFormik = (row) => {
    if (!row) return;
    formik.setValues(normalizeInitialValues(row));
  };

  const updateExecutionUser = useCallback(async (executionUserId) => {
    if (!formik.values?.id) return null;
    const resp = await postRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/execution`, {
      execution_user_id: executionUserId || null,
    }, true);
    return resp?.data || null;
  }, [formik.values?.id]);

  const updateDeadline = useCallback(async (deadline) => {
    if (!formik.values?.id) return null;
    const resp = await postRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/deadline`, {
      deadline: deadline || null,
    }, true);
    return resp?.data || null;
  }, [formik.values?.id]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    const run = async () => {
      const hasRowFromList = !!customerRow && Number(customerRow?.id) === Number(customerId);
      if (hasRowFromList) {
        formik.setValues(normalizeInitialValues(customerRow));
      }
      try {
        // Always fetch full detail payload; list row often does not contain
        // all CRM status fields needed for read-only checkbox sections.
        const row = await loadDetail(customerId);
        if (!active) return;
        const normalized = normalizeInitialValues(row || customerRow || null);
        formik.setValues(normalized);
        await loadContacts(normalized.id || customerId);
        const scheduleNavList = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
          ? (cb) => window.requestIdleCallback(cb, { timeout: 2500 })
          : (cb) => window.setTimeout(cb, 400);
        scheduleNavList(() => {
          if (!active) return;
          ensureNavListLoaded().catch(() => {});
        });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('Common.LoadFailed'), life: 3000 });
      } finally {
        if (active) setDetailActivityLoading(false);
      }
    };
    run();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, customerRow, loadContacts, loadDetail, setDialogText, setVisible, t, toast]);

  const navRows = useMemo(() => (allRows || []).map((r) => ({ id: r.id, name: r.name })), [allRows]);
  const nav = useMemo(() => createArrayNavigateHandlers(navRows, formik.values?.id || null), [navRows, formik.values?.id]);
  const navDisabled = !formik.values?.id || navRows.length < 2;

  const openById = useCallback(async (id) => {
    if (!id) return;
    setDetailActivityLoading(true);
    try {
      await ensureNavListLoaded();
      const row = await loadDetail(id);
      if (row) {
        formik.setValues(normalizeInitialValues(row));
        loadContacts(row.id || id);
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('Common.LoadFailed'), life: 3000 });
    } finally {
      setDetailActivityLoading(false);
    }
  }, [formik, ensureNavListLoaded, loadContacts, loadDetail, t, toast]);

  const scrollToFirst = () => {
    const first = nav.first();
    if (first?.id) openById(first.id);
  };
  const scrollToLast = () => {
    const last = nav.last();
    if (last?.id) openById(last.id);
  };
  const scrollUp = () => {
    const prev = nav.prev();
    if (prev?.id) openById(prev.id);
  };
  const scrollDown = () => {
    const next = nav.next();
    if (next?.id) openById(next.id);
  };

  const reloadCurrent = async () => {
    if (!formik.values?.id) return;
    setDetailActivityLoading(true);
    try {
      const row = await loadDetail(formik.values.id);
      if (row) {
        formik.setValues(normalizeInitialValues(row));
        await loadContacts(row.id);
      }
      toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('Common.DataReloaded'), life: 2500 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('Common.LoadFailed'), life: 3000 });
    } finally {
      setDetailActivityLoading(false);
    }
  };

  const printDetail = () => {
    window.print();
  };

  const openAddNew = () => {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerAllPartnersList.Title'), translationKey: 'CustomerAllPartnersList.Title', legacyLabel: t('CustomerAllPartnersList.Title'),
      page: 'CustomerAllPartnersList',
      props: {},
      mask: 'cust016',
    });
  };

  const copyCurrent = () => {
    toast?.current?.show({ severity: 'warn', summary: t('Common.Info'), detail: t('Common.FeatureUnavailable', { label: t('Common.Copy') }), life: 2200 });
  };

  const openContactDialog = (row = null) => {
    setEditingContact(row || null);
    setContactForm({
      name: row?.name || '',
      tel_number: row?.tel_number || '',
      email: row?.email || '',
      description: row?.description || '',
    });
    setContactDialogVisible(true);
  };

  const applyBuyerColumnVisibility = useCallback((table, jsonColumns) => {
    if (!table || !jsonColumns || typeof jsonColumns !== 'object') return;
    const next = { ...columnsVisibleBuyers, ...jsonColumns };
    setColumnsVisibleBuyers(next);
    Object.keys(next).forEach((field) => {
      if (next[field] === false) table.hideColumn?.(field);
      else table.showColumn?.(field);
    });
    table.redraw?.(true);
  }, [columnsVisibleBuyers]);

  const applyBuyerMaskFromState = useCallback(() => {
    const table = buyersTableRef.current;
    if (!table) return;
    isRestoringBuyerMaskRef.current = true;
    const effective = getEffectiveMask(PAGE_BUYERS, state);
    try {
      if (effective?.type === 'user' && effective.userMask) {
        applyUserMaskRowToTabulator(table, effective.userMask, {
          setColumnsVisible: (v) => setColumnsVisibleBuyers((prev) => ({ ...prev, ...(v || {}) })),
          clearHeaderFiltersWhenEmpty: true,
          applyColumnVisibilityFn: (tbl, jc) => applyBuyerColumnVisibility(tbl, jc),
        });
        return;
      }
      if (effective?.type === 'standard' && effective.standardMask) {
        const attributes = safeParse(effective.standardMask.attributes, []);
        const jsonColumns = safeParse(effective.standardMask.columns, {});
        const sorters = safeParse(effective.standardMask.sorters, []);
        if (Array.isArray(attributes) && attributes.length > 1) {
          try { table.setColumnLayout?.(attributes); } catch (_) {}
        }
        applyBuyerColumnVisibility(table, jsonColumns);
        if (Array.isArray(sorters) && sorters.length) {
          try { table.setSort?.(sorters); } catch (_) {}
        }
      }
    } finally {
      setTimeout(() => { isRestoringBuyerMaskRef.current = false; }, 0);
    }
  }, [applyBuyerColumnVisibility, state]);

  const onSelectBuyerRows = useCallback(() => {
    const selected = buyersTableRef.current?.getSelectedData?.() ?? [];
    setEditBuyerDisabled(selected.length !== 1);
    setDeleteBuyerDisabled(selected.length < 1);
  }, []);

  const saveContact = async () => {
    if (!formik.values?.id) return;
    if (!String(contactForm.name || '').trim()) {
      toast?.current?.show({ severity: 'warn', summary: t('Common.Warning'), detail: t('Common.Required'), life: 2500 });
      return;
    }
    setDialogText?.(t('Common.Saving'));
    setVisible?.(true);
    try {
      let saveResp;
      if (editingContact?.id) {
        saveResp = await postRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/contacts/update/${editingContact.id}`, contactForm, true);
      } else {
        saveResp = await postRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/contacts`, contactForm, true);
      }
      const savePayload = saveResp?.data ?? saveResp;
      const savedRows = Array.isArray(savePayload?.data) ? savePayload.data : (Array.isArray(savePayload) ? savePayload : null);
      if (savedRows) {
        setContacts(savedRows);
      } else {
        await loadContacts(formik.values.id);
      }
      const row = await loadDetail(formik.values.id);
      if (row) formik.setValues(normalizeInitialValues(row));
      setContactDialogVisible(false);
      setEditingContact(null);
      toast?.current?.show({
        severity: 'info',
        summary: t('ServiceTask.Confirmed'),
        detail: editingContact?.id ? t('CustomerCentralDatabaseAdd.Toast.Updated') : t('CustomerCentralDatabaseAdd.Toast.Saved'),
        life: 2200,
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3000 });
    } finally {
      setVisible?.(false);
    }
  };

  const editSelectedBuyer = () => {
    const selected = buyersTableRef.current?.getSelectedData?.() ?? [];
    if (selected.length !== 1) return;
    openContactDialog(selected[0]);
  };

  function BuyerCreatedAtCellFormatter(props) {
    const rowData = props?.cell?.getRow?.().getData?.() || {};
    const raw = rowData?.created_at;
    const user = rowData?.created_by_user;
    const userName = user?.employee?.name || user?.username || '';
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : formatBuyerDateTime(raw);
    return (
      <div className="flex items-center gap-2">
        {userName ? <Avatar className="shrink-0" title={userName} label={getUserInitials(userName)} shape="circle" style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff' }} /> : null}
        <span>{text || ''}</span>
      </div>
    );
  }

  function BuyerUpdatedAtCellFormatter(props) {
    const rowData = props?.cell?.getRow?.().getData?.() || {};
    const raw = rowData?.updated_at;
    const user = rowData?.updated_by_user;
    const userName = user?.employee?.name || user?.username || '';
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : formatBuyerDateTime(raw);
    return (
      <div className="flex items-center gap-2">
        {userName ? <Avatar className="shrink-0" title={userName} label={getUserInitials(userName)} shape="circle" style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff' }} /> : null}
        <span>{text || ''}</span>
      </div>
    );
  }

  function BuyerEditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openContactDialog(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  const removeSelectedBuyers = () => {
    const selected = buyersTableRef.current?.getSelectedData?.() ?? [];
    if (!selected.length) return;
    confirmDialog({
      message: t('Common.ConfirmDelete'),
      header: t('Common.Warning'),
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        if (!formik.values?.id) return;
        setDialogText?.(t('Common.LoadingData'));
        setVisible?.(true);
        try {
          for (const row of selected) {
            await deleteRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/contacts/${row.id}`, true);
          }
          const detail = await loadDetail(formik.values.id);
          if (detail) formik.setValues(normalizeInitialValues(detail));
          await loadContacts(formik.values.id);
          buyersTableRef.current?.deselectRow?.();
          setEditBuyerDisabled(true);
          setDeleteBuyerDisabled(true);
        } catch (e) {
          toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('Common.LoadFailed'), life: 3000 });
        } finally {
          setVisible?.(false);
        }
      },
    });
  };

  const setMask = (field, value) => {
    const table = buyersTableRef.current;
    if (!table) return;
    const next = { ...columnsVisibleBuyers, [field]: value };
    if (!Object.values(next).some(Boolean)) return;
    setColumnsVisibleBuyers(next);
    if (value === false) table.hideColumn?.(field);
    else table.showColumn?.(field);
    table.redraw?.(true);
  };

  const maskSelectAll = () => {
    const table = buyersTableRef.current;
    if (!table) return;
    const next = Object.keys(columnsVisibleBuyers).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setColumnsVisibleBuyers(next);
    Object.keys(next).forEach((field) => table.showColumn?.(field));
    table.redraw?.(true);
  };

  const maskDeselectAll = () => {
    const table = buyersTableRef.current;
    if (!table) return;
    const firstKey = Object.keys(columnsVisibleBuyers)[0];
    const next = Object.keys(columnsVisibleBuyers).reduce((acc, key) => ({ ...acc, [key]: key === firstKey }), {});
    setColumnsVisibleBuyers(next);
    Object.keys(next).forEach((field) => {
      if (next[field]) table.showColumn?.(field);
      else table.hideColumn?.(field);
    });
    table.redraw?.(true);
  };

  const saveBuyerMask = () => {
    const table = buyersTableRef.current;
    if (!table) return;
    setUserMaskFilter?.(
      PAGE_BUYERS,
      'set_columns',
      columnsVisibleBuyers,
      table.getHeaderFilters?.() ?? [],
      table.getColumnLayout?.() ?? null,
      cleanSorters(table.getSorters?.() ?? [])
    );
    setMaskVisible(false);
  };

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );
  const closeMenu = () => setExpandedKeys({});

  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.RefreshDetail'), icon: 'pi pi-refresh', command: () => { reloadCurrent(); closeMenu(); }, template: itemRenderer },
      {
        key: '0_2',
        label: t('ServiceTask.SetFilter'),
        icon: 'pi pi-filter',
        command: () => {
          const table = buyersTableRef.current;
          if (!table) return;
          setUserMaskFilter?.(PAGE_BUYERS, 'set_filters', null, table.getHeaderFilters?.() ?? [], null);
          closeMenu();
        },
        template: itemRenderer,
      },
      {
        key: '0_3',
        label: t('ServiceTask.ClearFilter'),
        icon: 'pi pi-filter-slash',
        command: () => {
          const table = buyersTableRef.current;
          if (!table) return;
          setUserMaskFilter?.(PAGE_BUYERS, 'set_filters', null, [], null);
          table.clearHeaderFilter?.();
          closeMenu();
        },
        template: itemRenderer,
      },
      {
        key: '0_4',
        label: t('ServiceTask.SetSort'),
        icon: 'pi pi-sort-alt',
        command: () => {
          const table = buyersTableRef.current;
          if (!table) return;
          setUserMaskFilter?.(PAGE_BUYERS, 'set_sorters', null, null, null, cleanSorters(table.getSorters?.() ?? []));
          closeMenu();
        },
        template: itemRenderer,
      },
      {
        key: '0_5',
        label: t('Common.ClearSorting'),
        icon: 'pi pi-sort-alt-slash',
        command: () => {
          const table = buyersTableRef.current;
          if (!table) return;
          setUserMaskFilter?.(PAGE_BUYERS, 'set_sorters', null, null, null, []);
          table.setSort?.([]);
          closeMenu();
        },
        template: itemRenderer,
      },
      { key: '0_6', label: t('Common.SetMask'), icon: 'pi pi-server', command: () => { setMaskVisible(true); closeMenu(); }, template: itemRenderer },
    ],
  }];

  return (
    <form onSubmit={formik.handleSubmit} className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <IntersectionObserverWrapper>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
          <button
            type="button"
            className="disabled:opacity-25"
            onClick={tabIndex === 0 ? () => openContactDialog(null) : openAddNew}
          >
            <AddIcon />
          </button>
        </div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
          <button
            type="button"
            className="disabled:opacity-25"
            disabled={tabIndex === 0 ? editBuyerDisabled : false}
            onClick={tabIndex === 0 ? editSelectedBuyer : copyCurrent}
          >
            {tabIndex === 0 ? <EditIcon /> : <CopyIcon />}
          </button>
        </div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
          <button
            type="button"
            className="disabled:opacity-25"
            disabled={tabIndex === 0 ? deleteBuyerDisabled : true}
            onClick={tabIndex === 0 ? removeSelectedBuyers : undefined}
          >
            <DeleteIcon />
          </button>
        </div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Print')} onClick={printDetail}><PrintIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Common.Refresh')} onClick={reloadCurrent}><RefreshIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollToFirst}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollUp}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollDown}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollToLast}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog?.(t('CustomerAllPartnersDetail.Title'), PAGE_ADD_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView?.(PAGE_ADD_KEY)}><PriceIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]" data-targetid="actions">
          <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
        </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="relative flex-1 min-h-0 min-w-0 overflow-hidden">
        <Splitter
          className="h-[calc(100vh_-_177px)]"
          layout="vertical"
          pt={{ gutter: { className: 'bg-[#333] h-8' }, gutterHandler: { className: 'bg-secondary' } }}
        >
          <SplitterPanel className="flex align-items-center justify-content-center min-h-0 min-w-0 overflow-x-auto overflow-y-auto" size={80} minSize={20}>
            <div className="w-full min-w-0 px-1 py-1 mb-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
                <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('CustomerAllPartnersDetail.Title')}</h1>
                {formType === 'edit' && <div className="text-md font-semibold text-gray-800 mt-1">{t('Common.RecordIdLabel')}: {formik.values.id}</div>}
                <div className="mt-4">
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Name')}</label>
                  <input disabled name="name" value={formik.values.name} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 md:gap-6 gap-4 mt-4">
                  <div className="md:col-span-4 min-w-0">
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.DefinedId')}</label>
                    <input disabled value={formik.values.defined_id || ''} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                    <p className="text-xs text-gray-500 mt-1">{t('BusinessPartnerAdd.DefinedIdHint')}</p>
                  </div>
                  <div className="md:col-span-8 min-w-0">
                    <label className="block text-md font-semibold text-gray-800">{t('CustomerCentralDatabase.Label.PartnerType')}</label>
                    <div className="grid grid-cols-2 py-2 bg-white border rounded-md mt-2 px-3">
                      <div className="flex align-items-center">
                        <Checkbox checked={formik.values.types?.includes('supplier')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                        <label className="ml-2">{t('BusinessPartnerAdd.Type.Supplier')}</label>
                      </div>
                      <div className="flex align-items-center">
                        <Checkbox checked={formik.values.types?.includes('calibration_company')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                        <label className="ml-2">{t('BusinessPartnerAdd.Type.CalibrationCompany')}</label>
                      </div>
                      <div className="flex align-items-center">
                        <Checkbox checked={formik.values.types?.includes('training_company')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                        <label className="ml-2">{t('BusinessPartnerAdd.Type.TrainingCompany')}</label>
                      </div>
                      <div className="flex align-items-center">
                        <Checkbox checked={formik.values.types?.includes('service_company')} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                        <label className="ml-2">{t('BusinessPartnerAdd.Type.ServiceCompany')}</label>
                      </div>
                      <div className="flex align-items-center">
                        <Checkbox checked={formik.values.party_mode === 'customer'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                        <label className="ml-2">{t('BusinessPartnerAdd.Type.Customer')}</label>
                      </div>
                      <div className="flex align-items-center">
                        <Checkbox checked={formik.values.party_mode === 'client'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                        <label className="ml-2">{t('BusinessPartnerAdd.Type.Client')}</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-3 bg-white border rounded-md">
                  <div className="grid grid-cols-1 md:grid-cols-12 md:gap-3 gap-2">
                    <div className="md:col-span-4 min-w-0">
                      <div>
                        <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.ReadOnly.CustomerState')}</label>
                        <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                          <div className="flex align-items-center">
                            <Checkbox inputId="detail_customer_state_occupied" checked={formik.values.customer_state === 'occupied'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                            <label htmlFor="detail_customer_state_occupied" className="ml-2">{t('CustomerCentralDatabase.State.Occupied')}</label>
                          </div>
                          <div className="flex align-items-center">
                            <Checkbox inputId="detail_customer_state_free" checked={formik.values.customer_state !== 'occupied'} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                            <label htmlFor="detail_customer_state_free" className="ml-2">{t('CustomerCentralDatabase.State.Free')}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-8 min-w-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:pl-1">
                        <div className="min-w-0">
                          <label className="block text-md font-semibold text-gray-800">{t('CustomerAllPartnersDetail.Label.CustomerType')}</label>
                          <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                            {customerTypeOptions.map((opt) => (
                              <div key={`detail_customer_type_${opt.value}`} className="flex align-items-center">
                                <Checkbox inputId={`detail_customer_type_${opt.value}`} checked={displayCustomerType === opt.value} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                                <label htmlFor={`detail_customer_type_${opt.value}`} className="ml-2">{customerClientTypeDisplayLabel(opt.label, locale)}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="min-w-0 sm:pl-1">
                          <label className="block text-md font-semibold text-gray-800">{t('CustomerAllPartnersDetail.Label.ClientType')}</label>
                          <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                            {clientTypeOptions.map((opt) => (
                              <div key={`detail_client_type_${opt.value}`} className="flex align-items-center">
                                <Checkbox inputId={`detail_client_type_${opt.value}`} checked={displayClientType === opt.value} onChange={() => {}} className="p-button-sm p-button-text pointer-events-none" />
                                <label htmlFor={`detail_client_type_${opt.value}`} className="ml-2">{customerClientTypeDisplayLabel(opt.label, locale)}</label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.ReadOnly.Trader')}</label>
                    <select
                      disabled
                      value={formik.values.trader_id || ''}
                      className="block w-full h-10 px-3 py-2 mt-2 border rounded-md bg-white"
                    >
                      <option value="">{t('CustomerCentralDatabaseAdd.SelectTrader')}</option>
                      <option value={formik.values.trader_id || ''}>{formik.values.trader_name || ''}</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.ContactPerson')}</label>
                  <input disabled name="contact_person" value={formik.values.contact_person} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                  <p className="text-xs text-gray-500 mt-1">{t('CustomerAllPartnersDetail.ContactPotentialClientHint')}</p>
                </div>
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Country')}</label>
                  <input disabled value={countryLabelFromCode(formik.values.country, locale)} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Address')}</label>
                  <input disabled name="address" value={formik.values.address} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Phone')}</label>
                  <input disabled name="tel_number" value={formik.values.tel_number} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Email')}</label>
                  <input disabled name="email" value={formik.values.email} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.WebAddress')}</label>
                  <input disabled name="web_address" value={formik.values.web_address} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
                <div>
                  <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Note')}</label>
                  <textarea disabled name="description" value={formik.values.description} className="block w-full min-h-[84px] px-4 py-2 mt-2 border rounded-md bg-white" />
                </div>
              </div>
              <CustomerBusinessStatusPanel
                customerId={formik.values?.id}
                comments={formik.values?.comments || []}
                mentions={formik.values?.mentions || []}
                executionUserId={formik.values?.execution_user_id}
                deadline={formik.values?.deadline}
                deadlineReminderCommentId={formik.values?.deadline_reminder_comment_id}
                usersForMentions={usersForMentions}
                loading={detailActivityLoading}
                disabled={false}
                canManageInterestedUsers={canManageInterestedUsers}
                canRemoveInterestedUsers={canRemoveInterestedUsers}
                title={t('CustomerAllPartnersDetail.BusinessCustomerStateTitle')}
                intro={t('CustomerAllPartnersDetail.BusinessCustomerStateIntro')}
                onDetailUpdated={mergeDetailIntoFormik}
                onChangeExecutionUser={updateExecutionUser}
                onChangeDeadline={updateDeadline}
              />
            </div>
            </div>
          </SplitterPanel>
          <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full min-w-0 overflow-x-auto overflow-y-auto" size={55} minSize={25} style={{ minHeight: '110px' }}>
            <div className="m-1 bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden h-full flex flex-col flex-1 min-h-0 w-full">
              <TabView className="maintenance-card-detail-tabview h-full flex-1 min-h-0" activeIndex={tabIndex} onTabChange={(e) => setTabIndex(e.index)}>
                <TabPanel className="h-full" header={t('CustomerAllPartnersDetail.Tab.Buyers')}>
                  <ReactTabulator
                    data={contacts}
                    onRef={bindBuyersTableRef}
                    className="h-full w-full"
                    columns={[
                      { formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', headerSort: false, width: 44, minWidth: 44, resizable: false, print: false, download: false },
                      { formatter: reactFormatter(<BuyerEditRowFormatter />), title: '', width: 44, minWidth: 44, hozAlign: 'center', headerSort: false, resizable: false, print: false, download: false },
                      { title: buyerHeaderTitle(t('ServiceTaskTable.Col.Id')), field: 'id', minWidth: 90, widthGrow: 0.4, headerFilter: 'input', headerFilterLiveFilter: false },
                      { title: buyerHeaderTitle(t('CustomerAllPartnersDetail.Col.BuyerName')), field: 'name', minWidth: 220, widthGrow: 1.1, headerFilter: 'input', headerFilterLiveFilter: false },
                      { title: buyerHeaderTitle(t('CustomerAllPartnersDetail.Col.BuyerDescription')), field: 'description', minWidth: 220, widthGrow: 1.2, headerFilter: 'input', headerFilterLiveFilter: false },
                      { title: buyerHeaderTitle(t('BusinessPartnerList.Column.TelNumber')), field: 'tel_number', minWidth: 180, widthGrow: 0.9, headerFilter: 'input', headerFilterLiveFilter: false },
                      { title: buyerHeaderTitle(t('BusinessPartnerList.Column.Email')), field: 'email', minWidth: 220, widthGrow: 1, headerFilter: 'input', headerFilterLiveFilter: false },
                      {
                        title: buyerHeaderTitle(t('BusinessPartnerList.Column.CreatedAt')),
                        field: 'created_at',
                        minWidth: tabulatorDateUserColumnMinWidth(t('BusinessPartnerList.Column.CreatedAt')),
                        widthGrow: 1.2,
                        sorter: 'datetime',
                        sorterParams: { format: 'iso' },
                        hozAlign: 'left',
                        formatter: reactFormatter(<BuyerCreatedAtCellFormatter />),
                        headerFilter: dateUserHeaderFilter,
                        headerFilterLiveFilter: false,
                        headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
                        headerFilterFunc: dateOrUserFilter,
                        headerFilterFuncParams: { name: 'created_at' },
                      },
                      {
                        title: buyerHeaderTitle(t('BusinessPartnerList.Column.UpdatedAt')),
                        field: 'updated_at',
                        minWidth: tabulatorDateUserColumnMinWidth(t('BusinessPartnerList.Column.UpdatedAt')),
                        widthGrow: 1.2,
                        sorter: 'datetime',
                        sorterParams: { format: 'iso' },
                        hozAlign: 'left',
                        formatter: reactFormatter(<BuyerUpdatedAtCellFormatter />),
                        headerFilter: dateUserHeaderFilter,
                        headerFilterLiveFilter: false,
                        headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt },
                        headerFilterFunc: dateOrUserFilter,
                        headerFilterFuncParams: { name: 'updated_at' },
                      },
                    ]}
                    events={{
                      rowSelectionChanged: onSelectBuyerRows,
                      tableBuilt: () => {
                        applyBuyerMaskFromState();
                        syncBuyersTableAlert();
                      },
                      ...getTabulatorPersistLayoutEvents(PAGE_BUYERS, buyersTableRef, setUserMaskFilter),
                      columnMoved: () => {
                        if (isRestoringBuyerMaskRef.current) return;
                        setUserMaskFilter?.(PAGE_BUYERS, 'column_move', null, null, buyersTableRef.current?.getColumnLayout?.());
                      },
                    }}
                    options={{
                      layout: 'fitColumns',
                      movableColumns: true,
                      headerSortTristate: true,
                      selectable: true,
                      selectableRows: true,
                      height: '100%',
                      dataLoader: true,
                      dataLoaderLoading: t('Common.LoadingData'),
                      dataLoaderError: t('Common.LoadFailed'),
                    }}
                  />
                </TabPanel>
                <TabPanel className="h-full" header={t('CustomerAllPartnersDetail.Tab.Communication')}>
                  {tabIndex === 1 ? (
                  <CrmCommunicationTicketsList
                    embedded
                    crmTicketsLayout
                    mineOnlyDefault={false}
                    maskCode={CRM_COMM_MASK_MY_AGENDA}
                    companyId={formik.values.id}
                    types="announcement,acquisition"
                    listPageKey={CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_COMMUNICATION}
                  />
                  ) : null}
                </TabPanel>
                <TabPanel className="h-full" header={t('CustomerAllPartnersDetail.Tab.InquiriesOffers')}>
                  {tabIndex === 2 ? (
                  <CrmCommunicationTicketsList
                    embedded
                    crmTicketsLayout
                    mineOnlyDefault={false}
                    maskCode={CRM_COMM_MASK_MY_AGENDA}
                    companyId={formik.values.id}
                    types="quote_request"
                    listPageKey={CRM_COMM_PAGE_CUSTOMER_ALL_PARTNERS_DETAIL_QUOTES}
                  />
                  ) : null}
                </TabPanel>
              </TabView>
            </div>
          </SplitterPanel>
        </Splitter>
      </div>
      <button type="submit" ref={submitRef} className="hidden" />

      <Dialog
        header={editingContact?.id ? t('Common.Edit') : t('CustomerAllPartnersDetail.Action.AddBuyer')}
        visible={contactDialogVisible}
        style={{ width: '40vw' }}
        onHide={() => setContactDialogVisible(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" className="h-10 px-4 py-2 text-sm border rounded-md" onClick={() => setContactDialogVisible(false)}>{t('Common.Cancel')}</button>
            <button type="button" className="h-10 px-4 py-2 text-sm bg-zinc-900 text-white rounded-md" onClick={saveContact}>{t('Common.Save')}</button>
          </div>
        )}
      >
        <div className="grid gap-3">
          <div>
            <label className="block text-md font-semibold text-gray-800">{t('CustomerAllPartnersDetail.Col.BuyerName')}</label>
            <input value={contactForm.name} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} className="block w-full h-10 px-4 py-2 mt-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800">{t('CustomerAllPartnersDetail.Col.BuyerDescription')}</label>
            <input value={contactForm.description} onChange={(e) => setContactForm((p) => ({ ...p, description: e.target.value }))} className="block w-full h-10 px-4 py-2 mt-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerList.Column.TelNumber')}</label>
            <input value={contactForm.tel_number} onChange={(e) => setContactForm((p) => ({ ...p, tel_number: e.target.value }))} className="block w-full h-10 px-4 py-2 mt-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerList.Column.Email')}</label>
            <input value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} className="block w-full h-10 px-4 py-2 mt-2 border rounded-md" />
          </div>
        </div>
      </Dialog>
      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('Common.SetMask')}
        fields={['id', 'name', 'description', 'tel_number', 'email', 'created_at', 'updated_at']}
        checkedMap={columnsVisibleBuyers}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveBuyerMask}
        selectAllLabel={t('Common.SelectAll')}
        deselectAllLabel={t('Common.DeselectAll')}
        saveLabel={t('Common.Save')}
        getFieldLabel={(field) => {
          if (field === 'name') return t('CustomerAllPartnersDetail.Col.BuyerName');
          if (field === 'description') return t('CustomerAllPartnersDetail.Col.BuyerDescription');
          if (field === 'id') return t('ServiceTaskTable.Col.Id');
          if (field === 'tel_number') return t('BusinessPartnerList.Column.TelNumber');
          if (field === 'email') return t('BusinessPartnerList.Column.Email');
          if (field === 'created_at') return t('BusinessPartnerList.Column.CreatedAt');
          if (field === 'updated_at') return t('BusinessPartnerList.Column.UpdatedAt');
          return field;
        }}
        inputIdPrefix="crm-my-customers-buyers-mask"
      />
    </form>
  );
}
